import * as THREE from 'three/webgpu';

/**
 * @file Renderer construction + backend capability probing.
 * NEVER import bare 'three' — `three/webgpu` for classes, `three/tsl` for nodes.
 */

/** @typedef {import('../types.js').EngineCaps} EngineCaps */

/**
 * True when the page URL asks for the WebGL2 fallback (`?forceWebGL=1`).
 * Kept here so every entry point (engine, editor, gallery) agrees.
 * @returns {boolean}
 */
export function wantsWebGLFallback() {
  if (typeof window === 'undefined') return false;
  const v = new URLSearchParams(window.location.search).get('forceWebGL');
  return v === '1' || v === 'true';
}

/**
 * Creates the WebGPU renderer (auto-falls back to WebGL2 during `init()`).
 *
 * IMPORTANT: three r185 only decides the real backend inside `await
 * renderer.init()`. The `caps` object returned here holds a synchronous *guess*
 * and is MUTATED IN PLACE by {@link refreshCaps} once init resolves — hold the
 * object reference, do not destructure `caps.webgpu` before init.
 * `Engine.init()` does this for you.
 *
 * @param {HTMLCanvasElement} canvas
 * @param {{antialias?: boolean, alpha?: boolean, forceWebGL?: boolean, pixelRatio?: number}} [opts]
 * @returns {{ renderer: THREE.WebGPURenderer, caps: EngineCaps }}
 */
export function createRenderer(canvas, opts = {}) {
  const { antialias = true, alpha = false, forceWebGL = false, pixelRatio } = opts;

  const useWebGL = forceWebGL || wantsWebGLFallback();

  const renderer = new THREE.WebGPURenderer({
    canvas,
    antialias,
    alpha,
    forceWebGL: useWebGL,
    // Hybrid-GPU laptops: without this hint browsers hand WebGPU the low-power
    // iGPU adapter. Forwarded to navigator.gpu.requestAdapter() by the backend.
    powerPreference: 'high-performance',
  });

  // No tone mapping: the bake already produces final colours, and >1 values are
  // deliberate bloom fuel. Output colour space stays sRGB (three's default).
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.setPixelRatio(
    pixelRatio ?? (typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1),
  );
  // No shadow maps anywhere in this project — lighting is CPU-baked.
  if (renderer.shadowMap) renderer.shadowMap.enabled = false;

  /** @type {EngineCaps} */
  const caps = {
    webgpu: !useWebGL && typeof navigator !== 'undefined' && !!navigator.gpu,
    instancedSprite: !useWebGL && typeof navigator !== 'undefined' && !!navigator.gpu,
    maxTextureSize: 8192,
    maxAnisotropy: 1,
    backend: useWebGL ? 'webgl2' : 'webgpu',
  };

  return { renderer, caps };
}

/**
 * Fills `caps` from the live backend. Call once, after `await renderer.init()`.
 * Mutates and returns the same object.
 * @param {THREE.WebGPURenderer} renderer
 * @param {EngineCaps} caps
 * @returns {EngineCaps}
 */
export function refreshCaps(renderer, caps) {
  const backend = /** @type {any} */ (renderer).backend;
  const isWebGPU = backend?.isWebGPUBackend === true;

  caps.webgpu = isWebGPU;
  // The instanced `THREE.Sprite` + SpriteNodeMaterial path is WebGPU-only.
  // Everything that draws billboards must gate on this flag and fall back to
  // InstancedMesh + billboarding() on WebGL2.
  caps.instancedSprite = isWebGPU;
  caps.backend = isWebGPU ? 'webgpu' : 'webgl2';
  caps.maxTextureSize = readMaxTextureSize(backend);

  try {
    caps.maxAnisotropy = renderer.getMaxAnisotropy?.() ?? 1;
  } catch {
    caps.maxAnisotropy = 1;
  }

  return caps;
}

/**
 * One-line console banner: backend + three revision. Guarded so it prints once
 * per page even under React StrictMode's double mount.
 * @param {EngineCaps} caps
 */
export function logBackendOnce(caps) {
  const g = /** @type {any} */ (globalThis);
  if (g.__TDLP_BACKEND_LOGGED__) return;
  g.__TDLP_BACKEND_LOGGED__ = true;
  const label = caps.webgpu ? 'WebGPU' : 'WebGL2 fallback';
  console.log(
    `[engine] backend: ${label} · three r${THREE.REVISION} · maxTexture ${caps.maxTextureSize} · maxAniso ${caps.maxAnisotropy}`,
  );
}

/**
 * @param {any} backend
 * @returns {number}
 */
function readMaxTextureSize(backend) {
  try {
    if (backend?.isWebGPUBackend === true) {
      const lim = backend.device?.limits?.maxTextureDimension2D;
      if (typeof lim === 'number' && lim > 0) return lim;
      return 8192;
    }
    const gl = backend?.gl;
    if (gl) return gl.getParameter(gl.MAX_TEXTURE_SIZE) || 4096;
  } catch {
    /* capability probing must never break startup */
  }
  return 4096;
}
